import {
  cacheKeys,
  canModifyUserRole,
  getFullName,
  Group,
  Name,
  PermissionValues,
  Phone,
  RoleKey,
  RoleNameMap,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentAccount,
  useCustomMutation,
  useRoles,
} from '@gripp/shared-logic';
import {
  Button,
  CustomAlert,
  getFormattedPhoneNumber,
  GroupSelector,
  Pill,
  TextInput as TextInputCropCircle,
  tokens,
  typography,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, Text, View } from 'react-native';

export type PeopleDetailAccount = {
  id: string;
  name?: Name;
  email?: string;
  jobTitle?: string;
  phone?: Phone;
  groups?: Group[];
  workspaceRoles?: Array<{ workspace: string; role: string }>;
};

type AccountDetailPanelProps = {
  account: PeopleDetailAccount;
  workspaceId: string;
  showGlobalAdminRole: boolean;
  onRemoved: () => void;
};

export const AccountDetailPanel = ({
  account,
  workspaceId,
  showGlobalAdminRole,
  onRemoved,
}: AccountDetailPanelProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { account: currentUser } = useCurrentAccount();
  const { roles } = useRoles();
  const { groups: availableGroups } = useAllWorkspaceGroups(workspaceId);
  const { createGroup } = useCreateGroup();

  const initialRoleId = useMemo(
    () =>
      account.workspaceRoles?.find((wr) => wr.workspace === workspaceId)?.role,
    [account.workspaceRoles, workspaceId]
  );
  const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>(
    initialRoleId
  );

  const initialGroups = useMemo<Group[]>(
    () =>
      (account.groups ?? []).map((g) => ({ id: g.id, name: g.name } as Group)),
    [account.groups]
  );
  const [selectedGroups, setSelectedGroups] = useState<Group[]>(initialGroups);

  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const { mutateAsync: changeRole, isPending: isChangingRole } =
    useCustomMutation({
      query: `
        mutation ChangeRole($input: ChangeRoleInput!) {
          account {
            changeRole(input: $input) { id }
          }
        }`,
    });

  const { mutateAsync: updateAccountsGroups, isPending: isUpdatingGroups } =
    useCustomMutation({
      query: `
        mutation UpdateAccountsGroups($input: UpdateAccountsGroupsInput!) {
          account {
            updateAccountsGroups(input: $input) { success message }
          }
        }`,
    });

  const { mutateAsync: removeFromWorkspace, isPending: isRemoving } =
    useCustomMutation({
      query: `
        mutation RemoveFromWorkspace($input: RemoveFromWorkspaceInput!) {
          account {
            removeFromWorkspace(input: $input) { success }
          }
        }`,
    });

  const currentUserRoleName = useMemo(() => {
    const wr = currentUser?.workspaceRoles?.find(
      (r) => r.workspace === workspaceId
    );
    if (!wr) return undefined;
    return roles.find((r) => r.id === wr.role)?.name;
  }, [currentUser, roles, workspaceId]);

  const hasChangeRolePermission =
    currentUser?.hasPermission(PermissionValues.account.changeRole) ?? false;

  const rolePillList = useMemo(
    () =>
      roles.filter(
        (r) =>
          showGlobalAdminRole || r.name !== RoleNameMap[RoleKey.GLOBAL_ADMIN]
      ),
    [roles, showGlobalAdminRole]
  );

  const invalidateAccounts = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: cacheKeys('account').all,
    });
  }, [queryClient]);

  const onRoleChange = useCallback(
    async (newRoleId: string) => {
      if (newRoleId === selectedRoleId) return;
      const prev = selectedRoleId;
      setSelectedRoleId(newRoleId);
      try {
        await changeRole({
          input: { id: account.id, workspaceId, role: newRoleId },
        });
        await invalidateAccounts();
      } catch (err) {
        setSelectedRoleId(prev);
        throw err;
      }
    },
    [selectedRoleId, account.id, workspaceId, changeRole, invalidateAccounts]
  );

  const onGroupsChange = useCallback(
    async (next: Group[]) => {
      const prev = selectedGroups;
      const prevIds = new Set(prev.map((g) => g.id));
      const nextIds = new Set(next.map((g) => g.id));
      const added = next.filter((g) => !prevIds.has(g.id));
      const removed = prev.filter((g) => !nextIds.has(g.id));
      if (added.length === 0 && removed.length === 0) return;
      setSelectedGroups(next);
      try {
        await updateAccountsGroups({
          input: {
            addedGroups: added.map((g) => ({ id: g.id })),
            removedGroups: removed.map((g) => ({ id: g.id })),
            accountIds: [account.id],
            workspaceId,
          },
        });
        await invalidateAccounts();
      } catch (err) {
        setSelectedGroups(prev);
        throw err;
      }
    },
    [
      selectedGroups,
      account.id,
      workspaceId,
      updateAccountsGroups,
      invalidateAccounts,
    ]
  );

  const onConfirmRemove = useCallback(async () => {
    await removeFromWorkspace({
      input: { id: account.id, workspaceId },
    });
    await invalidateAccounts();
    setShowRemoveConfirm(false);
    onRemoved();
  }, [
    account.id,
    workspaceId,
    removeFromWorkspace,
    invalidateAccounts,
    onRemoved,
  ]);

  const fullName = useMemo(
    () => getFullName(account.name) || '',
    [account.name]
  );
  const phoneDisplay = useMemo(() => {
    const p = account.phone;
    if (!p?.phoneNumber) return '';
    return getFormattedPhoneNumber(p.countryCode, p.callingCode, p.phoneNumber);
  }, [account.phone]);

  return (
    <View style={styles.container} testID="account-detail-panel">
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <TextInputCropCircle
          label={t('account.list.name')}
          value={fullName}
          disabled
        />
        <TextInputCropCircle
          label={t('account.list.jobTitle')}
          value={account.jobTitle ?? ''}
          disabled
        />
        <TextInputCropCircle
          label={t('account.list.phone')}
          value={phoneDisplay}
          disabled
        />
        <TextInputCropCircle
          label={t('account.list.email')}
          value={account.email ?? ''}
          disabled
        />

        <View style={styles.section}>
          <Text style={styles.sectionLabel}>
            {t('account.list.accountType').toUpperCase()}
          </Text>
          <View style={styles.pillsRow}>
            {rolePillList.map((role) => {
              const isCurrent = role.id === selectedRoleId;
              const hierarchyAllows = currentUserRoleName
                ? canModifyUserRole(currentUserRoleName, role.name)
                : false;
              const disabled =
                isChangingRole ||
                !hasChangeRolePermission ||
                (!isCurrent && !hierarchyAllows);
              return (
                <Pill
                  key={role.id}
                  label={role.name}
                  selected={isCurrent}
                  disabled={disabled}
                  showIcon={false}
                  onPress={() => {
                    void onRoleChange(role.id);
                  }}
                />
              );
            })}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionLabel}>
            {t('account.list.groups').toUpperCase()}
          </Text>
          <GroupSelector
            selectedGroups={selectedGroups}
            availableGroups={availableGroups ?? []}
            onGroupsChange={(next: Group[]) => {
              void onGroupsChange(next);
            }}
            onCreateGroup={createGroup}
            disabled={isUpdatingGroups || !hasChangeRolePermission}
          />
        </View>
      </ScrollView>

      <View style={styles.removeButtonContainer}>
        <Button
          variant="rectangular"
          state="outline"
          size="large"
          disabled={isRemoving || !hasChangeRolePermission}
          loading={isRemoving}
          onPress={() => setShowRemoveConfirm(true)}
          testID="account-detail-remove-button"
        >
          {t('account.detail.removeAccount')}
        </Button>
      </View>

      <CustomAlert
        visible={showRemoveConfirm}
        title={t('account.menu.confirmDelete.title', { accountName: fullName })}
        primaryButtonLabel={t('account.menu.confirmDelete.primaryButton')}
        secondaryButtonLabel={t('account.menu.confirmDelete.secondaryButton')}
        action={() => {
          void onConfirmRemove();
        }}
        hideAlert={() => setShowRemoveConfirm(false)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.lg,
    gap: tokens.spacing.lg,
  },
  section: {
    gap: tokens.spacing.sm,
  },
  sectionLabel: {
    ...typography.label.small,
    color: tokens.colors.neutral[600],
    letterSpacing: 0.5,
  },
  pillsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.xs,
  },
  removeButtonContainer: {
    padding: tokens.spacing.lg,
    paddingTop: tokens.spacing.md,
  },
});
